import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Box from '@material-ui/core/Box';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DashboardIcon from '@material-ui/icons/Dashboard';
import { makeStyles } from '@material-ui/core/styles';
import jwt_decode from 'jwt-decode';

import { buildMenu, showDashboard } from './menuConfig';
import { menuFontSize, menuIconSize } from './themes';

const useStyles = makeStyles(theme => ({
    /**
     * A second bar sitting directly under the app bar.
     *
     * Deliberately NOT sticky and with no negative top margin. The app bar's
     * real height varies with its contents (the Logo makes it taller than
     * react-admin's 48px dense toolbar), so any hardcoded offset here ends up
     * tucking the first few pixels of this bar underneath it. Sitting in
     * normal flow cannot overlap, whatever the app bar height turns out to be.
     */
    bar: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        minHeight: 40,
        boxSizing: 'border-box',
        // cancel react-admin's horizontal content padding so the bar runs
        // edge to edge (content uses paddingLeft 5, padding spacing(3))
        width: 'auto',
        alignSelf: 'stretch',
        marginLeft: -5,
        marginRight: -theme.spacing(3),
        marginBottom: theme.spacing(1),
        // mirror the app bar's own toolbar gutters so the two rows line up:
        // react-admin renders its Toolbar with disableGutters and
        // paddingRight: 24, and its menu button carries marginLeft '0.2em'
        paddingLeft: 0,
        paddingRight: 24,
        // Match the sidebar: it is a plain Drawer, so it takes
        // background.paper with text.primary. Using the same two values here
        // keeps the two menu layouts identical in colour, in both themes.
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        // keep it scrollable on mid-width screens without showing a scrollbar
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
    },
    button: {
        color: 'inherit',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        minWidth: 'auto',
        // 12px matches the IconButton padding used by the app bar's menu
        // button, so this row's icons sit on the same vertical line
        padding: '4px 12px',
        fontSize: menuFontSize,
        '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
            fontSize: menuIconSize,
        },
        '&:first-child': {
            marginLeft: '0.2em',
        },
    },
    menuPaper: {
        marginTop: 2,
    },
    itemIcon: {
        minWidth: 32,
    },
    itemText: {
        fontSize: menuFontSize,
    },
}));

/**
 * Horizontal navigation bar shown under the app bar when the menu layout
 * is 'top'. Reads the same tree as the sidebar (menuConfig.js), so
 * permissions and links cannot drift between the two.
 */
const TopMenu = () => {
    const classes = useStyles();
    const history = useHistory();
    const [anchor, setAnchor] = useState({ key: null, el: null });
    useSelector(state => state.theme); // rerender on theme change

    const decoded = localStorage.token ? jwt_decode(localStorage.token) : null;
    const level = decoded ? decoded.level : 0;

    if (!decoded || Number(level) === 1) { return null; }

    const context = {
        level,
        code: decoded.code || '',
        branch_code: decoded.branch_code || '',
        userid: decoded.id || 0,
    };

    const sections = buildMenu(context);

    const openSection = (key, event) => setAnchor({ key, el: event.currentTarget });
    const close = () => setAnchor({ key: null, el: null });

    const go = to => {
        close();
        history.push(to);
    };

    return (
        <Box className={classes.bar}>
            {showDashboard(context) && (
                <Button
                    className={classes.button}
                    startIcon={<DashboardIcon />}
                    onClick={() => go('/')}
                >
                    Dashboard
                </Button>
            )}

            {sections.map(section => {
                const id = section.key + section.name;
                return (
                    <React.Fragment key={id}>
                        <Button
                            className={classes.button}
                            startIcon={<section.icon />}
                            endIcon={<ExpandMoreIcon />}
                            onClick={event => openSection(id, event)}
                        >
                            {section.name}
                        </Button>

                        <Menu
                            anchorEl={anchor.el}
                            open={anchor.key === id}
                            onClose={close}
                            classes={{ paper: classes.menuPaper }}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                            {section.items.map((item, index) =>
                                section.external ? (
                                    <MenuItem
                                        key={item.key}
                                        component="a"
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={close}
                                        divider={index < section.items.length - 1}
                                    >
                                        <ListItemText
                                            primary={item.label}
                                            primaryTypographyProps={{ className: classes.itemText }}
                                        />
                                    </MenuItem>
                                ) : (
                                    <MenuItem key={item.key} onClick={() => go(item.to)}>
                                        <ListItemIcon className={classes.itemIcon}>
                                            <item.icon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.label}
                                            primaryTypographyProps={{ className: classes.itemText }}
                                        />
                                    </MenuItem>
                                )
                            )}
                        </Menu>
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

export default TopMenu;
